import React, { useCallback, useEffect, useState } from "react";
import { FileText, Image as ImageIcon, RotateCcw, Upload } from "lucide-react";
import { fetchBrandingAsset, openDocumentFile, practiceApi } from "../lib/practiceApi";
import { usePlan } from "./PlanBanner";

const ASSETS = [
  ["logo", "logo_path", "Logo dello studio", "Compare nell'intestazione di ogni PDF e documento Word.", true],
  ["signature", "signature_path", "Timbro e firma", "Inserito sopra la riga di firma del professionista.", false],
  ["watermark", "watermark_path", "Filigrana", "Immagine di sfondo in basso a destra dei documenti.", true],
];

export function BrandingAssets({ studio, onChange, onError, onDone }) {
  const [previews, setPreviews] = useState({});
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(false);
  const plan = usePlan(onError);
  const pro = plan ? plan.pro !== false : true;

  async function openPreview() {
    setPreview(true); onError("");
    try {
      await openDocumentFile("/settings/document-preview", "Anteprima documento.pdf", "pdf");
    } catch (err) { onError(err.message); } finally { setPreview(false); }
  }

  const reload = useCallback(async (kind) => {
    const url = await fetchBrandingAsset(kind);
    setPreviews(current => {
      if (current[kind]) URL.revokeObjectURL(current[kind]);
      return { ...current, [kind]: url };
    });
  }, []);

  useEffect(() => { ASSETS.forEach(([kind]) => reload(kind)); }, [reload]);

  async function upload(kind, field, file) {
    if (!file) return;
    setBusy(kind); onError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const result = await practiceApi(`/settings/asset/${kind}`, { method: "POST", body: form });
      onChange(field, result[field] || "");
      await reload(kind);
      onDone("Immagine aggiornata: sarà usata nei prossimi documenti");
    } catch (err) { onError(err.message); } finally { setBusy(""); }
  }

  async function reset(kind, field) {
    setBusy(kind); onError("");
    try {
      await practiceApi(`/settings/asset/${kind}`, { method: "DELETE" });
      onChange(field, "");
      await reload(kind);
      onDone("Immagine predefinita ripristinata");
    } catch (err) { onError(err.message); } finally { setBusy(""); }
  }

  return <div className="settings-assets" data-testid="settings-branding">
    {ASSETS.map(([kind, field, label, hint, proOnly]) => <div className="settings-asset" key={kind} data-testid={`settings-asset-${kind}`}>
      <div className="settings-asset-head">
        <strong><ImageIcon size={15} /> {label} {proOnly && !pro && <span className="pro-badge" data-testid={`pro-badge-${kind}`}>Pro</span>}</strong>
        <small>{proOnly && !pro ? "Nel piano Standard è l'immagine ingego" : (studio[field] ? "Immagine personalizzata attiva" : "In uso l'immagine predefinita")}</small>
      </div>
      <div className="settings-asset-preview">
        {previews[kind]
          ? <img src={previews[kind]} alt={label} data-testid={`settings-asset-preview-${kind}`} />
          : <span className="muted">Nessuna immagine</span>}
      </div>
      <p className="settings-asset-hint">{hint}</p>
      <div className="settings-asset-actions">
        {proOnly && !pro
          ? <span className="settings-asset-hint" data-testid={`settings-asset-locked-${kind}`}>Sbloccalo con ingego Pro dalla scheda <b>Colori documenti</b>.</span>
          : <>
            <label className="settings-upload" data-testid={`settings-asset-upload-${kind}`}>
              <Upload size={15} />{busy === kind ? "Caricamento…" : "Carica"}
              <input type="file" accept="image/png,image/jpeg,image/webp" hidden disabled={busy === kind}
                onChange={e => upload(kind, field, e.target.files?.[0])} />
            </label>
            {studio[field] && <button type="button" className="settings-asset-reset" disabled={busy === kind}
              data-testid={`settings-asset-reset-${kind}`} onClick={() => reset(kind, field)}>
              <RotateCcw size={14} />Ripristina
            </button>}
          </>}
      </div>
    </div>)}
      <div className="settings-asset-actions" style={{ gridColumn: "1 / -1", justifyContent: "flex-start" }}>
        <button type="button" className="primary" data-testid="settings-preview-document" disabled={preview}
          onClick={openPreview}><FileText size={15} />{preview ? "Generazione…" : "Anteprima documento di prova"}</button>
        <span className="settings-asset-hint">Apre un PDF di esempio con logo, tabella, filigrana e blocco firme.</span>
      </div>
  </div>;
}
